import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { copyToClipboard } from "@/lib/utils";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { RotateCcw, Copy, Trash2 } from "lucide-react";

export function TextReverser() {
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);

  const sample = "Antigravity Dev Hub";

  useEffect(() => {
    setText(sample);
  }, []);

  const reversed = text.split("").reverse().join("");

  const handleCopy = () => {
    copyToClipboard(reversed).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Text Reverser Online - Flip Strings Backwards"
      description="Instantly reverse your text or string. Flip characters backwards easily with our free online tool."
      h1="Text Reverser"
      category="Text & Word"
      categoryPath="/text-word"
      howToUse={[
        "Type or paste your text into the input box.",
        "The reversed text appears instantly in the output area."
      ]}
      faq={[
        { question: "How do you reverse text?", answer: "Type your text into our online tool and it will automatically flip it for you." },
        { question: "Does this reverse word order?", answer: "This tool reverses the character order. For word-order reversal, you'd need a different tool." }
      ]}
    >
      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Original Text</Label>
            <Textarea value={text} onChange={e => setText(e.target.value)} placeholder="Type here..." className="min-h-[220px]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Reversed Output</Label>
            <Textarea value={reversed} readOnly className="min-h-[220px] bg-slate-50 font-mono text-indigo-600 font-medium" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleCopy} disabled={!reversed} variant="outline" className="gap-2">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy Reversed"}
          </Button>
          <Button onClick={() => setText(sample)} variant="outline" className="gap-2">
            <RotateCcw className="h-4 w-4" />
            Reset Sample
          </Button>
          <Button onClick={() => setText("")} variant="ghost" className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
            Clear
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}

const SAMPLE_UNSORTED_LINES = `Cherry
Banana
Apple
Elderberry
Date`;

export function TextSorter() {
  const [text, setText] = useState("");
  const [output, setOutput] = useState("");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [copied, setCopied] = useState(false);

  const sortLines = (val = text, currentOrder = order) => {
    let lines = val.split("\n").filter(line => line.trim() !== "");
    lines.sort();
    if (currentOrder === "desc") lines.reverse();
    setOutput(lines.join("\n"));
  };

  useEffect(() => {
    setText(SAMPLE_UNSORTED_LINES);
    sortLines(SAMPLE_UNSORTED_LINES, order);
  }, []);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Text Line Sorter - Alphabetize Lists Online"
      description="Sort your text lines alphabetically (A-Z or Z-A) with our free online tool."
      h1="Text Line Sorter"
      category="Text & Word"
      categoryPath="/text-word"
      howToUse={[
        "Paste your list of lines into the input area.",
        "Choose 'A-Z' (ascending) or 'Z-A' (descending) order.",
        "Click 'Sort Lines' to alphabetize your list."
      ]}
      faq={[
        { question: "How to sort lines alphabetically?", answer: "Paste your text into our tool, choose the order, and click 'Sort Lines'." },
        { question: "Can I sort lines in reverse order?", answer: "Yes, select 'Z-A' mode to sort lines in descending order." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <div className="flex gap-2">
            <Button onClick={() => { setOrder("asc"); sortLines(text, "asc"); }} variant={order === "asc" ? "default" : "outline"} size="sm">A-Z Ascending</Button>
            <Button onClick={() => { setOrder("desc"); sortLines(text, "desc"); }} variant={order === "desc" ? "default" : "outline"} size="sm">Z-A Descending</Button>
          </div>
          <Button onClick={() => sortLines()} className="bg-slate-900 hover:bg-slate-800 text-white" size="sm">Sort Lines</Button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Original Lines</Label>
            <Textarea value={text} onChange={e => { setText(e.target.value); sortLines(e.target.value, order); }} placeholder="Unsorted lines..." className="min-h-[220px]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Sorted Lines</Label>
            <Textarea value={output} readOnly className="min-h-[220px] bg-slate-50 font-mono text-slate-800" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleCopy} disabled={!output} variant="outline" className="gap-2">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy Sorted"}
          </Button>
          <Button 
            onClick={() => {
              setText(SAMPLE_UNSORTED_LINES);
              sortLines(SAMPLE_UNSORTED_LINES, order);
            }} 
            variant="outline" 
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset Sample
          </Button>
          <Button onClick={() => { setText(""); setOutput(""); }} variant="ghost" className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
            Clear
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}

const SAMPLE_FIND_REPLACE = "The red car parked next to the red barn.";

export function FindAndReplaceTool() {
  const [text, setText] = useState("");
  const [find, setFind] = useState("red");
  const [replace, setReplace] = useState("blue");
  const [useRegex, setUseRegex] = useState(false);
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const process = (valText = text, valFind = find, valReplace = replace, isRegex = useRegex) => {
    try {
      if (!valFind) { setOutput(valText); return; }
      if (isRegex) {
        const regex = new RegExp(valFind, 'g');
        setOutput(valText.replace(regex, valReplace));
      } else {
        setOutput(valText.split(valFind).join(valReplace));
      }
    } catch (e: any) {
      setOutput("Error: " + e.message);
    }
  };

  useEffect(() => {
    setText(SAMPLE_FIND_REPLACE);
    process(SAMPLE_FIND_REPLACE, find, replace, useRegex);
  }, []);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Find and Replace Text Online - Search & Replace Strings"
      description="Easily find and replace text within your documents. Supports plain text and regular expressions (regex)."
      h1="Find and Replace Text"
      category="Text & Word"
      categoryPath="/text-word"
      howToUse={[
        "Paste your original text.",
        "Enter the text you want to find and the text to replace it with.",
        "Optionally enable 'Regular Expression' for advanced matching.",
        "Click 'Replace All'."
      ]}
      faq={[
        { question: "How do I find and replace text?", answer: "Enter the search and replacement terms in our tool and click 'Replace All'." },
        { question: "Can I use regex for find and replace?", answer: "Yes, check the 'Use Regular Expression' box to enable advanced pattern matching." }
      ]}
    >
      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Find Pattern</Label>
            <Input value={find} onChange={e => { setFind(e.target.value); process(text, e.target.value, replace, useRegex); }} className="h-10 text-sm" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Replace With</Label>
            <Input value={replace} onChange={e => { setReplace(e.target.value); process(text, find, e.target.value, useRegex); }} className="h-10 text-sm" />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 select-none cursor-pointer">
            <input type="checkbox" checked={useRegex} onChange={e => { setUseRegex(e.target.checked); process(text, find, replace, e.target.checked); }} className="w-4 h-4 rounded border-slate-300 text-indigo-600" />
            Use Regular Expression (Regex)
          </label>
          <Button onClick={() => process()} className="w-full sm:w-auto sm:ml-auto bg-slate-900 hover:bg-slate-800 text-white font-medium h-10">Replace All</Button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Original Text</Label>
            <Textarea value={text} onChange={e => { setText(e.target.value); process(e.target.value, find, replace, useRegex); }} placeholder="Original text..." className="min-h-[200px]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Modified Output</Label>
            <Textarea value={output} readOnly className="min-h-[200px] bg-slate-50 font-medium text-slate-800" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleCopy} disabled={!output} variant="outline" className="gap-2">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy Output"}
          </Button>
          <Button 
            onClick={() => {
              setText(SAMPLE_FIND_REPLACE);
              setFind("red");
              setReplace("blue");
              process(SAMPLE_FIND_REPLACE, "red", "blue", useRegex);
            }} 
            variant="outline" 
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset Sample
          </Button>
          <Button onClick={() => { setText(""); setOutput(""); }} variant="ghost" className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
            Clear
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function RandomGenerator() {
  const [type, setType] = useState<"words" | "sentences">("words");
  const [count, setCount] = useState(5);
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const wordList = ["apple", "banana", "cat", "dog", "elephant", "fish", "grape", "hat", "ice", "jump", "kite", "lemon", "mango", "nectarine", "orange", "pear"];
  const sentenceList = [
    "The quick brown fox jumps over the lazy dog.", 
    "A journey of a thousand miles begins with a single step.", 
    "To be or not to be, that is the question.",
    "Rapid prototyping allows design validation before assembly.",
    "Web developer software utilities run 100% locally in RAM."
  ];

  const generate = (valType = type, valCount = count) => {
    let result = [];
    const limit = valCount || 1;
    for(let i=0; i<limit; i++) {
      if (valType === "words") {
        result.push(wordList[Math.floor(Math.random() * wordList.length)]);
      } else {
        result.push(sentenceList[Math.floor(Math.random() * sentenceList.length)]);
      }
    }
    setOutput(result.join(valType === "words" ? " " : "\n"));
  };

  useEffect(() => {
    generate(type, count);
  }, []);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Random Text Generator - Generate Randomized Words & Sentences"
      description="Generate random words or sentences for testing, design layouts, or creative writing practice."
      h1="Random Text Generator"
      category="Text & Word"
      categoryPath="/text-word"
      howToUse={[
        "Choose 'Words' or 'Sentences'.",
        "Set the count of items you want.",
        "Click 'Generate' to create your random text."
      ]}
      faq={[
        { question: "How to generate random text?", answer: "Use our random text generator tool to select word or sentence types and generate them instantly." },
        { question: "Can I use this for placeholder text?", answer: "Yes, it is great for placeholder text in design drafts." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="space-y-1.5 flex-1">
            <Label className="text-sm font-semibold text-slate-700">Generator Type</Label>
            <select value={type} onChange={(e: any) => { setType(e.target.value); generate(e.target.value, count); }} className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none">
              <option value="words">Words</option>
              <option value="sentences">Sentences</option>
            </select>
          </div>
          <div className="space-y-1.5 flex-1">
            <Label className="text-sm font-semibold text-slate-700">Count</Label>
            <Input type="number" value={count} onChange={e => { const v = Number(e.target.value) || 1; setCount(v); generate(type, v); }} className="h-10 text-sm" />
          </div>
          <Button onClick={() => generate()} className="w-full sm:w-auto h-10 mt-6 sm:mt-0 bg-slate-900 hover:bg-slate-800 text-white font-medium">Generate Text</Button>
        </div>
        <Textarea value={output} readOnly className="min-h-[220px] bg-slate-50 font-medium text-slate-800 leading-relaxed p-4" />
        <div className="flex gap-2">
          <Button onClick={handleCopy} disabled={!output} variant="outline" className="gap-2">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy Text"}
          </Button>
          <Button onClick={() => { setType("words"); setCount(5); generate("words", 5); }} variant="outline" className="gap-2">
            <RotateCcw className="h-4 w-4" />
            Reset Sample
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}
